import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Star,
  User,
  Building
} from 'lucide-react';

const ReviewManager = () => {
  const [reviews, setReviews] = useState([
    {
      id: 1,
      name: 'Ahmet Yılmaz',
      company: 'TechCorp GmbH',
      role: 'CEO',
      rating: 5,
      content: 'CO2 Neutral ile web sitemizi karbon nötr hale getirdik. Müşterilerimiz artık çevre dostu bir şirketle çalıştıklarını biliyor ve bu bize güven veriyor.',
      language: 'tr',
      approved: true,
      date: '2024-01-15'
    },
    {
      id: 2,
      name: 'Maria Schmidt',
      company: 'GreenStart Solutions',
      role: 'Marketing Director',
      rating: 5,
      content: 'Sertifika süreci çok kolaydı. Teknik ekip her adımda bize yardımcı oldu ve sonuçta harika bir karbon nötr web sitemiz oldu.',
      language: 'de',
      approved: true,
      date: '2024-01-20'
    },
    {
      id: 3,
      name: 'Mehmet Kaya',
      company: 'EcoTech Industries',
      role: 'CTO',
      rating: 4,
      content: 'Blockchain destekli şeffaf sistem sayesinde müşterilerimiz sertifikamızı kolayca doğrulayabiliyor. Bu güven oluşturuyor.',
      language: 'tr',
      approved: false,
      date: '2024-01-25'
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [newReview, setNewReview] = useState({
    name: '',
    company: '',
    role: '',
    rating: 5,
    content: '',
    language: 'tr',
    approved: false
  });

  const handleAddReview = () => {
    const review = {
      id: Math.max(...reviews.map(r => r.id)) + 1,
      ...newReview,
      date: new Date().toISOString().split('T')[0]
    };
    setReviews([...reviews, review]);
    setShowAddModal(false);
    setNewReview({
      name: '',
      company: '',
      role: '',
      rating: 5,
      content: '',
      language: 'tr',
      approved: false
    });
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
  };

  const handleUpdateReview = () => {
    setReviews(reviews.map(review => 
      review.id === editingReview.id ? editingReview : review
    ));
    setEditingReview(null);
  };

  const handleDeleteReview = (id) => {
    if (window.confirm('Bu yorumu silmek istediğinizden emin misiniz?')) {
      setReviews(reviews.filter(review => review.id !== id));
    }
  };

  const handleToggleApproval = (id) => {
    setReviews(reviews.map(review => 
      review.id === id ? { ...review, approved: !review.approved } : review
    ));
  };

  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         review.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'approved' && review.approved) ||
                         (statusFilter === 'pending' && !review.approved);
    return matchesSearch && matchesStatus;
  });

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-neutral-300'}`}
      />
    ));
  };

  const getLanguageLabel = (language) => {
    switch (language) {
      case 'tr': return 'Türkçe';
      case 'de': return 'Deutsch';
      case 'en': return 'English';
      default: return language;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-neutral-800">Müşteri Yorumları Yönetimi</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Yeni Yorum</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
        <div className="flex space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input
                type="text"
                placeholder="İsim veya şirket ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">Tüm Yorumlar</option>
            <option value="approved">Onaylanmış</option>
            <option value="pending">Beklemede</option>
          </select>
        </div>
      </div>

      {/* Reviews Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredReviews.map((review) => (
          <div key={review.id} className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-800">{review.name}</h3>
                  <div className="flex items-center space-x-2 text-sm text-neutral-600">
                    <Building className="h-3 w-3" />
                    <span>{review.company}</span>
                  </div>
                  <p className="text-xs text-neutral-500">{review.role}</p>
                </div>
              </div>
              <div className="flex space-x-1">
                {renderStars(review.rating)}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-neutral-700 leading-relaxed">{review.content}</p>
            </div>

            <div className="flex items-center justify-between text-xs text-neutral-500 mb-4">
              <span>{getLanguageLabel(review.language)}</span>
              <span>{new Date(review.date).toLocaleDateString('tr-TR')}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditReview(review)}
                  className="text-primary-600 hover:text-primary-900 p-1"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteReview(review.id)}
                  className="text-red-600 hover:text-red-900 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => handleToggleApproval(review.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  review.approved
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                }`}
              >
                {review.approved ? 'Onaylı' : 'Beklemede'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Review Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">Yeni Yorum Ekle</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  İsim
                </label>
                <input
                  type="text"
                  value={newReview.name}
                  onChange={(e) => setNewReview({...newReview, name: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Şirket
                </label>
                <input
                  type="text"
                  value={newReview.company}
                  onChange={(e) => setNewReview({...newReview, company: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Pozisyon
                </label>
                <input
                  type="text"
                  value={newReview.role}
                  onChange={(e) => setNewReview({...newReview, role: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Puan
                </label>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewReview({...newReview, rating: star})}
                      className="p-1"
                    >
                      <Star
                        className={`h-6 w-6 ${
                          star <= newReview.rating ? 'text-yellow-400 fill-current' : 'text-neutral-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Dil
                </label>
                <select
                  value={newReview.language}
                  onChange={(e) => setNewReview({...newReview, language: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="tr">Türkçe</option>
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Yorum
                </label>
                <textarea
                  value={newReview.content}
                  onChange={(e) => setNewReview({...newReview, content: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="approved"
                  checked={newReview.approved}
                  onChange={(e) => setNewReview({...newReview, approved: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
                <label htmlFor="approved" className="ml-2 block text-sm text-neutral-700">
                  Onaylı
                </label>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleAddReview}
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Ekle
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 py-2 px-4 rounded-lg transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Review Modal */}
      {editingReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">Yorum Düzenle</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  İsim
                </label>
                <input
                  type="text"
                  value={editingReview.name}
                  onChange={(e) => setEditingReview({...editingReview, name: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Yorum
                </label>
                <textarea
                  value={editingReview.content}
                  onChange={(e) => setEditingReview({...editingReview, content: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit-approved"
                  checked={editingReview.approved}
                  onChange={(e) => setEditingReview({...editingReview, approved: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
                <label htmlFor="edit-approved" className="ml-2 block text-sm text-neutral-700">
                  Onaylı
                </label>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleUpdateReview}
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Güncelle
              </button>
              <button
                onClick={() => setEditingReview(null)}
                className="flex-1 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 py-2 px-4 rounded-lg transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewManager; 